import { useCallback, useRef, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { setNewRelicCustomAttribute } from '~/helpers/newrelic';
import { trackEvent } from '~/helpers/tracking';
import { selectChatbotStatus } from '~/redux/selectors/chatbot';
import clientSide from '~/helpers/clientSide';
import {
  getChatAction,
  createTrackingData,
  collectElements,
  attachEventListeners,
  filterElementsInChatbot,
  isWithinDebounceWindow,
  extractChatUserId,
  CHATBOT_SELECTORS,
  INPUT_SELECTORS,
} from './helpers';

interface UseChatbotTrackingProps {
  visible: boolean;
  currentPage: string;
}

interface useChatbotTrackingReturnType {
  isTracking: boolean;
  listenerCount: number;
}

export const useChatbotTracking = ({ visible, currentPage }: UseChatbotTrackingProps): useChatbotTrackingReturnType => {
  const chatBotStatus = useSelector(selectChatbotStatus);
  const chatbotType =
    chatBotStatus === 'PROACTIVE_CHAT_VISIBLE'
      ? 'ProactiveChat'
      : chatBotStatus === 'GIGA_CHAT_VISIBLE'
      ? 'GigaChat'
      : 'Disabled';

  const listenersAttachedRef = useRef(new Set<Element>());
  const lastClickTimeRef = useRef(0);
  const userIdTrackedRef = useRef(false);
  const [detectedUserId, setDetectedUserId] = useState<string | null>(null);

  const handleChatbotClick = useCallback(
    (event: Event) => {
      // Debounce protection
      if (isWithinDebounceWindow(lastClickTimeRef?.current)) return;
      lastClickTimeRef.current = Date.now();

      const target = event?.target as HTMLElement;
      const action = getChatAction(target);

      const trackingData = createTrackingData('elementClicked', chatbotType, currentPage, {
        text: action || target?.tagName,
        position: target?.tagName === 'IMG' ? 'open' : target?.className,
        htmlId: target?.id,
      });

      trackEvent(trackingData);

      // No need to check here - useEffect will handle it
    },
    [currentPage, chatbotType]
  );

  const handleInputEvent = useCallback(
    (event: Event) => {
      const target = event?.target as HTMLInputElement | HTMLTextAreaElement;

      if (!target?.value?.length) return;

      const trackingData = createTrackingData('fieldInputted', chatbotType, currentPage, {
        text: 'message_input',
        position: target?.className,
        htmlId: target?.id,
      });

      trackEvent(trackingData);
    },
    [currentPage, chatbotType]
  );

  const attachListeners = useCallback(() => {
    // Attach click listeners to chatbot elements
    const chatbotElements = collectElements(CHATBOT_SELECTORS);
    attachEventListeners(chatbotElements, 'click', handleChatbotClick, listenersAttachedRef?.current);

    // Attach input listeners to form elements within chatbot containers
    const inputElements = collectElements(INPUT_SELECTORS);
    const chatbotInputs = filterElementsInChatbot(inputElements);

    attachEventListeners(chatbotInputs, 'input', handleInputEvent, listenersAttachedRef?.current);
    attachEventListeners(chatbotInputs, 'keydown', handleInputEvent, listenersAttachedRef?.current);
  }, [handleChatbotClick, handleInputEvent]);

  const trackView = useCallback(() => {
    if (!visible) return;

    const trackingData = createTrackingData('elementViewed', chatbotType, currentPage);
    trackEvent(trackingData);
    setNewRelicCustomAttribute([{ name: `viewed${chatbotType}`, value: true }]);
  }, [visible, currentPage, chatbotType]);

  const setupTracking = useCallback(() => {
    if (!visible) return;

    let retryCount = 0;
    const maxRetries = 20;

    const checkForChatbot = () => {
      if (window?.KoreChatSDK) {
        setTimeout(() => {
          attachListeners();

          // Start checking for userId after SDK loads
          if (!userIdTrackedRef.current) {
            let checkCount = 0;
            const checkInterval = setInterval(() => {
              const userId = extractChatUserId();
              if (userId && userId !== detectedUserId) {
                setDetectedUserId(userId);
                clearInterval(checkInterval);
              } else if (checkCount++ > 20) {
                clearInterval(checkInterval);
              }
            }, 500);
          }
        }, 1000);
      } else if (retryCount < maxRetries) {
        retryCount++;
        setTimeout(checkForChatbot, 500);
      } else {
        console?.warn('Chatbot SDK failed to load after maximum retries');
      }
    };

    checkForChatbot();
  }, [visible, attachListeners, detectedUserId]);

  const cleanup = useCallback(() => {
    listenersAttachedRef?.current?.forEach((element) => {
      element?.removeEventListener('click', handleChatbotClick);
      element?.removeEventListener('input', handleInputEvent);
      element?.removeEventListener('keydown', handleInputEvent);
    });
    listenersAttachedRef?.current?.clear();
  }, [handleChatbotClick, handleInputEvent]);

  useEffect(() => {
    trackView();
    setupTracking();
    return cleanup;
  }, [trackView, setupTracking, cleanup]);

  const userId = detectedUserId || (clientSide && visible && window?.KoreChatSDK ? extractChatUserId() : null);

  useEffect(() => {
    if (!userId || userIdTrackedRef.current) return;

    trackEvent({
      action: 'chatIdentified',
      data: {
        chatUserId: userId,
      },
    });
    setNewRelicCustomAttribute([{ name: 'chatUserId', value: userId }]);
    userIdTrackedRef.current = true;
  }, [userId]);

  return {
    isTracking: visible,
    listenerCount: listenersAttachedRef?.current?.size,
  };
};
